// 抽奖转盘模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { consola } from "consola";
import { useUserStore } from "../../user/stores";
import type { WheelConfig } from "../types";
import { wheelApi } from "../api";

interface WheelState {
  config: WheelConfig | null;
  isLoaded: boolean;
  loading: boolean;
  /** 用户剩余可抽奖次数（未登录/未加载为 0），供入口红点等全局展示用 */
  spinCount: number;
}

export const useWheelStore = createWithEqualityFn<WheelState>()(() => ({
  config: null,
  isLoaded: false,
  loading: false,
  spinCount: 0,
}));

let loadingPromise: Promise<WheelConfig | null> | null = null;

/**
 * 加载转盘配置（带去重：已加载或加载中时不重复请求）
 */
export async function loadWheelConfig(): Promise<WheelConfig | null> {
  if (useWheelStore.getState().isLoaded) {
    return useWheelStore.getState().config;
  }
  if (loadingPromise) {
    return loadingPromise;
  }
  loadingPromise = (async () => {
    useWheelStore.setState({ loading: true });
    try {
      const config = await wheelApi.getConfig();
      useWheelStore.setState({ config, isLoaded: true, loading: false });
      return config;
    } catch (error) {
      // 静默失败：转盘入口属增强展示，请求失败不打扰用户
      consola.error("[wheel] 转盘配置加载失败:", error);
      useWheelStore.setState({ loading: false });
      return null;
    } finally {
      loadingPromise = null;
    }
  })();
  return loadingPromise;
}

let countLoadingPromise: Promise<number> | null = null;
/** 在途请求所属用户：换号后不复用旧请求，响应落地时比对以丢弃旧账号数据 */
let countLoadingUserId: string | null = null;

/**
 * 加载用户剩余可抽奖次数（按用户去重并发；每次调用都请求最新值，不做结果缓存）
 * @description 未登录不发请求；响应落地前比对发起时的 userId，期间登出/换号则
 *   丢弃响应，防旧账号的在途响应覆盖清零或串写到新账号（长挂请求下真实可达）
 */
export async function loadWheelSpinCount(): Promise<number> {
  const userId = useUserStore.getState().userInfo?.userId;
  // 未登录不请求：登出后节流 trailing/延迟补拉等触发源仍可能调进来
  if (!userId) {
    return useWheelStore.getState().spinCount;
  }
  // 仅同一用户的在途请求可复用；换号后发起自己的新请求
  if (countLoadingPromise && countLoadingUserId === userId) {
    return countLoadingPromise;
  }
  // let+null 初始化而非 const：finally 里要引用 promise 自身，const 会触发
  // TS2454(闭包内赋值前使用)。运行时安全：async 体在首个 await 处挂起，
  // finally 执行时赋值必已完成；极端情况下 promise 为 null 也只是跳过清锁
  let promise: Promise<number> | null = null;
  promise = (async () => {
    try {
      const info = await wheelApi.getInfo();
      // 身份竞态守卫：响应落地时已登出/已换号则丢弃
      if (useUserStore.getState().userInfo?.userId !== userId) {
        return useWheelStore.getState().spinCount;
      }
      const spinCount = info?.count ?? 0;
      useWheelStore.setState({ spinCount });
      return spinCount;
    } catch (error) {
      // 静默失败：红点属增强展示，请求失败不打扰用户
      consola.error("[wheel] 剩余抽奖次数加载失败:", error);
      return useWheelStore.getState().spinCount;
    } finally {
      // 换号后锁已被新用户的请求接管，只清自己这代的
      if (countLoadingPromise === promise) {
        countLoadingPromise = null;
        countLoadingUserId = null;
      }
    }
  })();
  countLoadingPromise = promise;
  countLoadingUserId = userId;
  return promise;
}

/** 同步剩余可抽奖次数（转盘弹窗内乐观扣减/服务端刷新时镜像到全局，红点实时联动） */
export function setWheelSpinCount(spinCount: number) {
  useWheelStore.setState({ spinCount: Math.max(0, spinCount) });
}

/** 清空剩余可抽奖次数（退出登录时调用） */
export function clearWheelSpinCount() {
  useWheelStore.setState({ spinCount: 0 });
}
